<script lang="ts">
  import type { Snippet } from 'svelte';

  /**
   * Dialog.svelte — a controlled modal dialog built on the native <dialog> element.
   */
  interface Props {
    /** Controls visibility. You own this state in the parent. */
    open?: boolean;
    /** Optional heading shown at the top of the dialog. */
    title?: string;
    /**
     * Optional custom heading content, rendered in place of the plain `title`
     * text (the × close button is kept). Use for rich headers — an icon, a
     * styled title, a subtitle, etc. Takes precedence over `title`.
     */
    titleContent?: Snippet;
    /** Called when the dialog closes (Esc, backdrop click, or the × button). */
    onclose?: () => void;
    /**
     * Optional guard run before a user-initiated close (Esc, backdrop, or the
     * built-in × button). Return false (or a promise of it) to keep the dialog
     * open — e.g. to confirm discarding unsaved changes first. When omitted the
     * dialog closes immediately.
     */
    confirmClose?: () => boolean | Promise<boolean>;
    /** The dialog body content. */
    children?: Snippet;
    /** Optional footer pinned below the scrollable body (e.g. action buttons). */
    footer?: Snippet;
  }

  let {
    open = false,
    title = '',
    titleContent,
    onclose,
    confirmClose,
    children,
    footer
  }: Props = $props();

  let dialog = $state<HTMLDialogElement>();

  // Keep the native dialog in sync with the `open` prop.
  $effect(() => {
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  });

  function handleClose() {
    onclose?.();
  }

  // Close the dialog, but let an optional guard veto first (used to confirm
  // discarding unsaved changes). Closing fires the native `close` event, which
  // routes through handleClose → onclose.
  async function tryClose() {
    if (confirmClose && !(await confirmClose())) return;
    dialog?.close();
  }

  // Native Esc fires a cancellable `cancel` event. With a guard, intercept it and
  // ask first; without one, let the dialog close as usual.
  function handleCancel(event: Event) {
    if (!confirmClose) return;
    event.preventDefault();
    tryClose();
  }

  // Clicking the backdrop (the dialog element itself, not its panel) closes it.
  function handleBackdropClick(event: MouseEvent) {
    if (event.target === dialog) tryClose();
  }
</script>

<dialog
  bind:this={dialog}
  class="dialog"
  onclose={handleClose}
  oncancel={handleCancel}
  onclick={handleBackdropClick}
  aria-labelledby={title && !titleContent ? 'dialog-title' : undefined}
>
  <div class="dialog__panel">
    {#if titleContent || title}
      <header class="dialog__header">
        {#if titleContent}
          {@render titleContent()}
        {:else}
          <h2 id="dialog-title" class="dialog__title">{title}</h2>
        {/if}
        <button type="button" class="dialog__close" aria-label="Close dialog" onclick={tryClose}>
          &times;
        </button>
      </header>
    {/if}

    <div class="dialog__body">
      {@render children?.()}
    </div>

    {#if footer}
      <footer class="dialog__footer">
        {@render footer()}
      </footer>
    {/if}
  </div>
</dialog>

<style>
  .dialog {
    /* Themeable via CSS custom properties */
    --dialog-bg: var(--background-header-input);
    --dialog-color: var(--text-primary);
    --dialog-radius: 12px;
    --dialog-backdrop: color-mix(in srgb, var(--background-site), transparent 80%);

    max-width: calc(100vw - 2rem);
    max-height: calc(100vh - 4rem);
    padding: 0;
    border: none;
    border-radius: var(--dialog-radius);
    background: var(--dialog-bg);
    color: var(--dialog-color);
    box-shadow: 0 10px 40px rgb(0 0 0 / 0.18);
    margin: auto;

    /* Open / close transition (native dialog, modern CSS) */
    opacity: 0;
    transform: scale(0.97) translateY(4px);
    transition:
      opacity 0.18s ease,
      transform 0.18s ease,
      overlay 0.18s ease allow-discrete,
      display 0.18s ease allow-discrete;
  }

  .dialog[open] {
    opacity: 1;
    transform: scale(1) translateY(0);
  }

  @starting-style {
    .dialog[open] {
      opacity: 0;
      transform: scale(0.97) translateY(4px);
    }
  }

  .dialog::backdrop {
    background: rgb(0 0 0 / 0);
    transition:
      background 0.18s ease,
      overlay 0.18s ease allow-discrete,
      display 0.18s ease allow-discrete;
  }

  .dialog[open]::backdrop {
    background: var(--dialog-backdrop);
  }

  @starting-style {
    .dialog[open]::backdrop {
      background: rgb(0 0 0 / 0);
    }
  }

  .dialog__panel {
    display: flex;
    flex-direction: column;
    max-height: inherit;
  }

  .dialog__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 20px 24px;
    padding-bottom: 0.75rem;
  }

  .dialog__title {
    margin: 0;
    color: var(--dialog-color);
    font-family: var(--font-primary);
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
  }

  .dialog__close {
    flex-shrink: 0;
    display: grid;
    place-items: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: inherit;
    font-size: 1.5rem;
    line-height: 1;
    cursor: pointer;
    transition: background 0.15s ease;
    color: var(--dialog-color);
  }

  .dialog__close:hover {
    background: rgb(0 0 0 / 0.06);
  }

  .dialog__close:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }

  .dialog__body {
    padding: 0 24px;
    padding-top: 0;
    overflow-y: auto;
  }

  .dialog__body:first-child {
    padding-top: var(--dialog-padding);
  }

  .dialog__footer {
    flex-shrink: 0;
    padding: 12px 24px 20px;
    border-top: 1px solid var(--devider);
  }
</style>
